<script setup lang="ts">
import { useRedirect } from "@core/composables"
import { useI18n } from "vue-i18n"
import { formatAddress } from "@core/helpers"
import { Address } from "@types"

const { t } = useI18n({
    messages: {
        fr: {
            title: "Liste des bâtiments",
            question: "À quel bâtiment souhaitez-vous accéder ?",
        },
    },
})

type BuildingNavTarget = "data" | "compteur"

type Props = {
    buildings: Partial<{
        _id: string
        name: string
        address: Address
        niveau: string
    }>[]
    /**
     * Sous-route bâtiment après : `_id_building` (défaut : page données)
     */
    target?: BuildingNavTarget
}

const props = withDefaults(defineProps<Props>(), {
    buildings: () => [],
    target: "data",
})

defineEmits(["click:building"])

const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

const { redirectWrapper } = useRedirect()

/**
 * Gère l'action à effectuer lors du clic sur un bâtiment.
 */
function onClickBuilding(building: Props["buildings"][0]) {
    const path =
        props.target === "compteur"
            ? "/building/:_id_building/compteur"
            : "/building/:_id_building/data"
    redirectWrapper(path, {
        _id_building: building._id,
    })
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        dismissable-mask
        position="top"
        class="modal-meter-building-list max-w-[35rem] w-full"
    >
        <template #header>
            <h2 class="text-lg text-primary font-semibold">
                {{ t("title") }}
            </h2>
        </template>
        <p class="modal-meter-building-list__title text-primary">
            {{ t("question") }}
        </p>
        <div class="modal-meter-building-list__content">
            <BaseButton
                v-for="(building, index) in buildings"
                :key="index"
                outlined
                class="modal-meter-building-list__content-element"
                @click="onClickBuilding(building)"
            >
                <BaseBuildingIcon :type="building.niveau" />
                <p
                    class="text-primary text-sm text-left modal-meter-building-list__source-name"
                >
                    <span v-if="building.name">{{ building.name }}</span>
                    <span>{{ formatAddress(building.address) }}</span>
                </p>
            </BaseButton>
        </div>
    </BaseDialog>
</template>

<style scoped>
.modal-meter-building-list {
    & .modal-meter-building-list__title {
        padding: 1rem 0;
    }
    & .modal-meter-building-list__content {
        --max-source-list: 5;
        --source-height: 4.5rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        max-height: calc(var(--max-source-list) * var(--source-height));
        overflow-y: auto;
    }
    & .modal-meter-building-list__content-element {
        --source-height: 4.5rem;
        justify-content: flex-start;
        gap: 0.5rem;
        min-height: var(--source-height);
        max-height: var(--source-height);
    }
    & .modal-meter-building-list__source-name {
        display: flex;
        flex-direction: column;
    }
}
</style>
